import React from 'react';
import { Users, Globe, Shield, TrendingUp, Award, Target } from 'lucide-react';
import businessTeamImage from '../assets/coaching-team.jpg';

const Differentiators = () => {
  const differentiators = [
    {
      icon: Users,
      title: 'Diversified Expertise',
      description: 'Our team strategically manages investments across Blockchain-based Assets, Real Estate, Oil and Gas, and Stocks, delivering a well-balanced portfolio and effective risk mitigation to support long-term growth.'
    },
    {
      icon: Globe,
      title: 'Tech Integration',
      description: 'We leverage cutting-edge technology and advanced analytics to provide real-time market insights and automated portfolio management solutions.'
    },
    {
      icon: TrendingUp,
      title: 'Global Insights',
      description: 'Access to international markets and global economic trends allows us to identify opportunities and manage risks across diverse geographical regions.'
    },
    {
      icon: Shield,
      title: 'Sustainable Practices',
      description: 'We prioritize sustainable and responsible investing, ensuring your portfolio aligns with environmental, social, and governance (ESG) principles.'
    },
    {
      icon: Target,
      title: 'Risk Management',
      description: 'Our comprehensive risk assessment and management protocols protect your investments while maintaining optimal growth potential through strategic diversification.'
    },
    {
      icon: Award,
      title: 'Client Focus',
      description: 'Every investment strategy is tailored to your unique financial goals, risk tolerance, and timeline, ensuring personalized service and optimal outcomes.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-gray-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f59e0b' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      <div className="relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
            What Makes Us
            <span className="text-gradient block">Different?</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            COREVEST stands apart in the investment industry through our unique 
            combination of expertise, technology, and personalized service delivery.
          </p>
        </div>

        {/* Hero Section with Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Image */}
          <div className="relative" data-aos="fade-right" data-aos-delay="100">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <img
                src={businessTeamImage}
                alt="Professional financial coaching and collaboration"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent"></div>
              
              {/* Overlay Badge */}
              <div className="absolute top-6 left-6">
                <div className="bg-gold-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Expert Guidance
                </div>
              </div>
            </div>
          </div>

          {/* Key Differentiators */}
          <div data-aos="fade-left" data-aos-delay="200">
            <h3 className="text-3xl font-bold text-navy-900 mb-8">
              Experience the COREVEST Difference
            </h3>
            <div className="space-y-6">
              {differentiators.slice(0, 3).map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-start space-x-4 group" data-aos="fade-up" data-aos-delay={300 + (index * 100)}>
                    <div className="flex-shrink-0">
                      <div className="inline-flex p-3 bg-gradient-to-r from-gold-500 to-gold-600 rounded-lg group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-navy-900 mb-2 group-hover:text-gold-600 transition-colors">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Additional Differentiators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {differentiators.slice(3).map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div
                key={index + 3}
                className="group bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:-translate-y-1"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <IconComponent className="h-8 w-8 text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-navy-900 mb-4 group-hover:text-gold-600 transition-colors">
                  {item.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>


        </div>
      </div>
      
      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden">
        <svg className="relative block w-full h-28" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <defs>
            <linearGradient id="differentiatorsWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{stopColor: '#f0f9ff', stopOpacity: 1}} />
              <stop offset="33%" style={{stopColor: '#e0f2fe', stopOpacity: 1}} />
              <stop offset="66%" style={{stopColor: '#bae6fd', stopOpacity: 1}} />
              <stop offset="100%" style={{stopColor: '#93c5fd', stopOpacity: 1}} />
            </linearGradient>
          </defs>
          <path 
            d="M0,20 C300,80 600,0 900,40 C1050,60 1150,20 1200,30 L1200,120 L0,120 Z" 
            fill="url(#differentiatorsWaveGradient)"
          />
          <path 
            d="M0,60 C250,20 500,100 750,50 C900,20 1100,80 1200,40 L1200,120 L0,120 Z" 
            fill="url(#differentiatorsWaveGradient)"
            opacity="0.6"
          />
        </svg>
      </div>
    </section>
  );
};

export default Differentiators; 